import {
  LanguageDto,
  LocalStorageKeys,
  UserDto,
  useDialog,
  usePrevious,
  useTheme,
} from '@ecdlink/core';
import { FileTypeEnum } from '@ecdlink/graphql';
import {
  ActionListDataItem,
  ActionModal,
  BannerWrapper,
  Button,
  Dialog,
  DialogPosition,
  FormInput,
  ProfileAvatar,
  renderIcon,
  StackedList,
  Typography,
  Dropdown,
  Avatar,
  IMAGE_WIDTH,
} from '@ecdlink/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { PhotoPrompt } from '@/components/photo-prompt/photo-prompt';
import { useDocuments } from '@/hooks/useDocuments';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { DialogFormInput } from '@/models/practitioner/DialogFormInput';
import {
  initialPractitionerAboutValues,
  PractitionerAboutModel,
  practitionerAboutModelSchema,
} from '@/schemas/practitioner/practitioner-about';
import { useAppDispatch } from '@store';
import { userActions, userSelectors, userThunkActions } from '@/store/user';
import { analyticsActions } from '@/store/analytics';
import { setStorageItem } from '@/utils/common/local-storage.utils';
import * as styles from '@/pages/practitioner/practitioner-about/practitioner-about.styles';
import ROUTES from '@/routes/routes';
import { staticDataSelectors } from '@/store/static-data';
import {
  healthCareWorkerSelectors,
  healthCareWorkerThunkActions,
} from '@/store/healthCareWorker';
import { ClinicDetails } from './components/clinicDetails/clinic-details';
import { EditCellPhoneNumber } from './edit-cellphone-number/edit-cellphone-number';
import { PractitionerAboutRouteState } from './practitioner-about.types';
import { BackToCommunityDialog } from './components/back-to-community-modal';
import { communitySelectors } from '@/store/community';
import { LeaderProfileRouteState } from '@/pages/community/team-tab/team/members/leader-profile/types';
import { cloneDeep } from '@apollo/client/utilities';

// Closing the camera / gallery emits a residual "ghost" click that lands
// wherever the finger was. It hits the freshly opened Looking good! dialog and
// dismisses it before it can be seen, so ignore dialog clicks fired within this
// window of the dialog opening.
const DIALOG_CLICK_GUARD_MS = 800;

export const PractitionerAbout: React.FC = () => {
  const version = process.env.REACT_APP_VERSION ?? '';
  const location = useLocation<PractitionerAboutRouteState>();
  const history = useHistory();
  const appDispatch = useAppDispatch();
  const { isOnline } = useOnlineStatus();
  const {
    userProfilePicture,
    deleteDocument,
    createNewDocument,
    updateDocument,
  } = useDocuments();

  const acceptedMimeFormats = [
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/heif',
  ];

  const [editFieldVisible, setEditFieldVisible] = useState(false);
  const [editLanguageFieldVisible, setEditLanguageFieldVisible] =
    useState(false);
  const [displayError, setDisplayError] = useState<boolean>(false);
  const [editProfilePictureVisible, setEditProfilePictureVisible] =
    useState(false);
  const [editiCellPhoneNumber, setEditiCellPhoneNumber] = useState(false);
  const user = useSelector(userSelectors.getUser);

  const healthCareWorker = useSelector(
    healthCareWorkerSelectors?.getHealthCareWorker
  );

  const languages = useSelector(staticDataSelectors.getLanguages);
  const clinic = useSelector(communitySelectors.getClinicSelector);

  const pictureStorageKey = LocalStorageKeys.practitionerProfilePicture;
  const [listItems, setListItems] = useState<ActionListDataItem[]>([]);
  const [clinicDetails, setClinicDetails] = useState(false);

  const dialog = useDialog();
  const isFromCommunityWelcome = location?.state?.isFromCommunityWelcome;
  const wasFromCommunityWelcome = usePrevious(isFromCommunityWelcome);

  const avatar =
    userProfilePicture?.file ||
    user?.profileImageUrl ||
    userProfilePicture?.reference;

  const getDefaultFormvalues = () => {
    if (user) {
      const tempPractitioner: PractitionerAboutModel = {
        name: user.firstName || '',
        surname: user.surname || '',
        cellphone: user.phoneNumber || '',
        email: user.email || '',
        languageId: user?.languageId || healthCareWorker?.languageId || '',
      };
      return tempPractitioner;
    } else {
      return initialPractitionerAboutValues;
    }
  };

  const {
    register: practitionerAboutRegister,
    formState: practitionerAboutFormState,
    getValues: practitionerAboutFormGetValues,
    setValue: practitionerAboutFormSetValue,
  } = useForm({
    resolver: yupResolver(practitionerAboutModelSchema),
    defaultValues: getDefaultFormvalues(),
    mode: 'onChange',
  });

  const [dialogFormInput, setDialogFormInput] = useState<
    DialogFormInput<PractitionerAboutModel>
  >({
    label: '',
    formFieldName: 'name',
    value: '',
  });

  const { theme } = useTheme();

  const setNewStackListItems = (currentUser: UserDto) => {
    const selectedLanguage = languages?.find(
      (item) => item?.id === practitionerAboutFormGetValues('languageId')
    );
    const list: ActionListDataItem[] = [
      {
        title: 'Cellphone Number',
        subTitle: currentUser?.phoneNumber || 'Add an Cellphone Number',
        switchTextStyles: true,
        actionName: currentUser?.phoneNumber ? 'Edit' : 'Add',
        actionIcon: currentUser?.phoneNumber ? 'PencilIcon' : 'PlusIcon',
        buttonType: 'filled',
        buttonColor: currentUser?.phoneNumber ? undefined : 'primary',
        textColor: currentUser?.phoneNumber ? undefined : 'white',
        onActionClick: () => {
          setEditiCellPhoneNumber(true);
        },
      },
      {
        title: 'Email Address',
        subTitle: currentUser?.email || 'Add an Email Address',
        switchTextStyles: true,
        actionName: currentUser?.email ? 'Edit' : 'Add',
        actionIcon: currentUser?.email ? 'PencilIcon' : 'PlusIcon',
        buttonType: 'filled',
        buttonColor: currentUser?.email ? undefined : 'primary',
        textColor: currentUser?.email ? undefined : 'white',
        onActionClick: () => {
          editField({
            label: 'Email Address',
            formFieldName: 'email',
            value: practitionerAboutFormGetValues().email,
          });
        },
      },
      {
        title: 'Your clinic & GGC team',
        subTitle: clinic?.name || '',
        switchTextStyles: true,
        actionName: 'View',
        actionIcon: 'EyeIcon',
        buttonType: 'filled',
        buttonColor: 'primary',
        textColor: 'white',
        onActionClick: () => {
          setClinicDetails(true);
        },
      },
      ...(!!clinic?.teamLeads?.length
        ? clinic?.teamLeads?.map(
            (leader, index) =>
              ({
                title: `Your Team Leader ${
                  clinic?.teamLeads.length > 1 ? index + 1 : ''
                }`,
                subTitle: `${leader?.firstName ?? ''} ${leader?.surname}`,
                switchTextStyles: true,
                actionName: 'View',
                actionIcon: 'EyeIcon',
                buttonType: 'filled',
                buttonColor: 'primary',
                textColor: 'white',
                onActionClick: () =>
                  history.push(
                    ROUTES.COMMUNITY.TEAM.MEMBERS.LEADER_PROFILE.replace(
                      ':leaderId',
                      leader?.id!
                    ),
                    { isFromAboutPage: true } as LeaderProfileRouteState
                  ),
              } as ActionListDataItem)
          )
        : [
            {
              title: 'Your Team Leader',
              subTitle: 'None',
              switchTextStyles: true,
            } as ActionListDataItem,
          ]),
      {
        title: 'Preferred language on app',
        subTitle: selectedLanguage?.description || 'Add a language',
        switchTextStyles: true,
        actionName: selectedLanguage?.description ? 'Edit' : 'Add',
        actionIcon: selectedLanguage?.description ? 'PencilIcon' : 'PlusIcon',
        buttonType: 'filled',
        buttonColor: selectedLanguage?.description ? undefined : 'primary',
        textColor: selectedLanguage?.description ? undefined : 'white',
        onActionClick: () => {
          editField({
            label: 'Language',
            formFieldName: 'languageId',
            value: practitionerAboutFormGetValues().languageId,
          });
        },
      },
    ];

    setListItems(list);
  };

  const editField = (
    formInputToLoad: DialogFormInput<PractitionerAboutModel>
  ) => {
    if (formInputToLoad.label === 'Language') {
      setDialogFormInput(formInputToLoad);
      return setEditLanguageFieldVisible(true);
    } else {
      setDialogFormInput(formInputToLoad);
      setEditFieldVisible(true);
    }
  };

  const saveEdit = async () => {
    if (practitionerAboutFormState.errors[dialogFormInput.formFieldName]) {
      setDisplayError(true);
    } else {
      setEditFieldVisible(false);
      setEditLanguageFieldVisible(false);
      savePractitionerUserData();
    }
  };

  const displayProfilePicturePrompt = useCallback(() => {
    setEditProfilePictureVisible(!editProfilePictureVisible);
  }, [editProfilePictureVisible]);

  const showLookingGoodDialog = useCallback(
    (photoUrl?: string) => {
      const photo = photoUrl || avatar;
      const openedAt = Date.now();
      const isResidualClick = () =>
        Date.now() - openedAt < DIALOG_CLICK_GUARD_MS;

      dialog({
        position: DialogPosition.Middle,
        blocking: true,
        color: 'bg-white',
        render: (onClose) => {
          const closeDialog = () => {
            if (isResidualClick()) return;
            onClose();
          };

          if (isFromCommunityWelcome) {
            return (
              <BackToCommunityDialog
                hideTitle={!photo}
                avatar={
                  photo ? (
                    <Avatar dataUrl={photo} size="header" className="mb-4" />
                  ) : undefined
                }
                onSubmit={() => {
                  if (isResidualClick()) return;

                  history.push(ROUTES.COMMUNITY.ROOT, {
                    isFromCommunityWelcome: false,
                  } as PractitionerAboutRouteState);
                  onClose();
                }}
                onClose={closeDialog}
              />
            );
          }

          return (
            <ActionModal
              className="bg-white"
              customIcon={
                photo ? (
                  <Avatar dataUrl={photo} size="header" className="mb-4" />
                ) : undefined
              }
              title="Looking good!"
              detailText="Your profile photo has been updated."
              actionButtons={[
                {
                  colour: 'primary',
                  text: 'Close',
                  textColour: 'white',
                  type: 'filled',
                  leadingIcon: 'CheckCircleIcon',
                  onClick: closeDialog,
                },
              ]}
            />
          );
        },
      });
    },
    [avatar, dialog, history, isFromCommunityWelcome]
  );

  const handlePicturePromptOnClose = () => {
    setEditProfilePictureVisible(false);

    // Community welcome flow: closing the picker without a photo still offers
    // the Looking good / return-to-community step when a photo already exists.
    if (isFromCommunityWelcome) {
      showLookingGoodDialog();
    }
  };

  const closeEditField = () => {
    setEditFieldVisible(false);
    setEditLanguageFieldVisible(false);
  };

  const deleteProfilePicture = () => {
    if (userProfilePicture) deleteDocument(userProfilePicture);

    const copy = Object.assign({}, user);
    if (copy) {
      copy.profileImageUrl = '';
      appDispatch(userActions.updateUser(copy));
    }

    if (!isFromCommunityWelcome) {
      setEditProfilePictureVisible(false);
    }
  };

  const picturePromtOnAction = async (imageBaseString: string) => {
    setStorageItem(imageBaseString, pictureStorageKey);
    setEditProfilePictureVisible(false);

    const copy = Object.assign({}, user);
    if (copy) {
      copy.profileImageUrl = imageBaseString;
      appDispatch(userActions.updateUser(copy));
    }

    // A failure here must not swallow the confirmation — the photo is already
    // applied to the user above, so the practitioner sees the new avatar either
    // way and would otherwise be dropped back on About me with no feedback.
    try {
      if (!userProfilePicture) {
        await createNewDocument({
          data: imageBaseString,
          userId: user?.id || '',
          fileType: FileTypeEnum.ProfileImage,
          fileName: `ProfilePicture_${user?.id}.png`,
        });
      } else {
        await updateDocument(userProfilePicture, imageBaseString);
      }
      await savePractitionerUserData(imageBaseString);
    } catch (error) {
      console.error('Failed to save profile photo', error);
    }

    // Camera green tick / gallery / emoji previously saved and closed with no
    // confirmation. Always show Looking good after a successful photo pick.
    showLookingGoodDialog(imageBaseString);
  };

  const savePractitionerUserData = (imageBaseString: string = '') => {
    const practitionerForm = practitionerAboutFormGetValues();
    const copy = cloneDeep(user);
    // const copy = Object.assign({}, user);
    if (copy) {
      copy.firstName = practitionerForm.name;
      copy.surname = practitionerForm.surname;
      copy.phoneNumber = practitionerForm.cellphone;
      copy.email = practitionerForm.email;
      copy.languageId = practitionerForm.languageId;
      if (imageBaseString?.length > 0) {
        copy.profileImageUrl = imageBaseString;
      }

      appDispatch(userActions.updateUser(copy));
      appDispatch(userThunkActions.updateUser(copy));
      appDispatch(
        healthCareWorkerThunkActions.updateHealthCareWorker({
          userId: user?.id!,
          input: {
            languageId: practitionerForm.languageId,
            isRegistered: healthCareWorker?.isRegistered!,
          },
        })
      );

      setNewStackListItems(copy);
    }
  };

  useEffect(() => {
    if (isFromCommunityWelcome && !wasFromCommunityWelcome) {
      displayProfilePicturePrompt();
    }
  }, [
    displayProfilePicturePrompt,
    isFromCommunityWelcome,
    wasFromCommunityWelcome,
  ]);

  useEffect(() => {
    if (!isOnline) {
      appDispatch(
        analyticsActions.createViewTracking({
          pageView: window.location.pathname,
          title: 'Practitioner About',
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  useEffect(() => {
    if (user) {
      setNewStackListItems(user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <div className={styles.container}>
      <Dialog fullScreen visible={clinicDetails} position={DialogPosition.Top}>
        <ClinicDetails setClinicDetails={setClinicDetails} />
      </Dialog>
      <Dialog
        fullScreen
        visible={editiCellPhoneNumber}
        position={DialogPosition.Top}
      >
        <EditCellPhoneNumber
          setEditiCellPhoneNumber={setEditiCellPhoneNumber}
          user={user}
        />
      </Dialog>
      <BannerWrapper
        showBackground={true}
        backgroundUrl={theme?.images.graphicOverlayUrl}
        backgroundImageColour={'primary'}
        title={'About me'}
        size="medium"
        renderBorder={true}
        renderOverflow={false}
        onBack={() => history.push(ROUTES.PRACTITIONER.PROFILE.ROOT)}
        displayOffline={!isOnline}
      >
        <div className={'inline-flex w-full justify-center pt-8'}>
          <ProfileAvatar
            dataUrl={userProfilePicture?.file || user?.profileImageUrl || ''}
            size={'header'}
            className="bg-secondary"
            onPressed={displayProfilePicturePrompt}
            hasConsent={true}
          />
        </div>
        <div>
          <StackedList
            className={'px-4'}
            listItems={listItems}
            type={'ActionList'}
          />
          <Typography
            type="help"
            className="px-4 pt-2"
            color="textLight"
            text={`Version ${version}`}
            weight="skinny"
          />
        </div>
      </BannerWrapper>

      <Dialog
        stretch={true}
        borderRadius="normal"
        visible={editFieldVisible}
        position={DialogPosition.Bottom}
      >
        <div className={'p-4'}>
          <div className={styles.labelContainer}>
            <Typography
              type="body"
              className=""
              color="textDark"
              text={dialogFormInput.label}
              weight="bold"
            />
            <div onClick={closeEditField}>
              {renderIcon('XIcon', 'h-6 w-6 text-uiLight')}
            </div>
          </div>
          <FormInput<PractitionerAboutModel>
            visible={true}
            nameProp={dialogFormInput.formFieldName}
            register={practitionerAboutRegister}
            disabled={false}
            className={!displayError ? 'mb-6' : ''}
          />
          {displayError && (
            <div className={'mt-2'}>
              <Typography
                type="help"
                color="errorMain"
                text={
                  practitionerAboutFormState.errors[
                    dialogFormInput.formFieldName
                  ]?.message || ''
                }
                className={'mb-6'}
              />
            </div>
          )}
          <Button
            type="filled"
            color="primary"
            className={'w-full'}
            onClick={saveEdit}
          >
            {renderIcon('SaveIcon', styles.buttonIcon)}
            <Typography
              type="help"
              className="mr-2"
              color="white"
              text={'Save'}
            />
          </Button>
        </div>
      </Dialog>
      <Dialog
        stretch={true}
        borderRadius="normal"
        visible={editLanguageFieldVisible}
        position={DialogPosition.Middle}
      >
        <div className={'p-4'}>
          <div className={styles.labelContainer}>
            <Typography
              type="body"
              className=""
              color="textDark"
              text={'Change preferred language on CHW Connect'}
              weight="bold"
            />
            <div onClick={closeEditField}>
              {renderIcon('XIcon', 'h-6 w-6 text-uiLight')}
            </div>
          </div>
          <Dropdown
            placeholder={'Choose language'}
            list={
              (languages &&
                languages.map((language: LanguageDto) => ({
                  label: language.description,
                  value: language.id || '',
                }))) ||
              []
            }
            fillType="clear"
            fullWidth={true}
            label={'Language'}
            selectedValue={practitionerAboutFormGetValues().languageId}
            onChange={(item: string) => {
              practitionerAboutFormSetValue('languageId', item, {
                shouldValidate: true,
              });
            }}
          />
          {displayError && (
            <div className={'mt-2'}>
              <Typography
                type="help"
                color="errorMain"
                text={
                  practitionerAboutFormState.errors[
                    dialogFormInput.formFieldName
                  ]?.message || ''
                }
                className={'mb-6'}
              />
            </div>
          )}
          <Button
            type="filled"
            color="primary"
            className={'mt-4 w-full'}
            onClick={saveEdit}
          >
            {renderIcon('SaveIcon', styles.buttonIcon)}
            <Typography
              type="help"
              className="mr-2"
              color="white"
              text={'Save'}
            />
          </Button>
        </div>
      </Dialog>
      <Dialog
        visible={editProfilePictureVisible}
        position={DialogPosition.Bottom}
      >
        <div className={'p-4'}>
          <PhotoPrompt
            acceptedMimeFormats={acceptedMimeFormats}
            resolutionLimit={IMAGE_WIDTH}
            title="Profile Photo"
            onClose={handlePicturePromptOnClose}
            onAction={picturePromtOnAction}
            onDelete={userProfilePicture ? deleteProfilePicture : undefined}
          />
        </div>
      </Dialog>
    </div>
  );
};
